import { useEffect, useState } from 'react'
import {
  Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, Table, TableBody, TableCell, TableHead, TableRow,
  TextField, Typography
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { callAxios } from '../hooks/useAxios'
import { DiagnosticsData, DiagnosticEntry, DeviceCommentEntry } from '../types/models'

interface DiagnosticsDialogProps {
  open: boolean
  onClose: () => void
  userId: number
  userEmail: string
}

export function DiagnosticsDialog({ open, onClose, userId, userEmail }: DiagnosticsDialogProps) {
  const { t } = useTranslation()
  const [data, setData] = useState<DiagnosticsData | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const [commentText, setCommentText] = useState('')
  const [bypassReason, setBypassReason] = useState<Record<number, string>>({})

  const fetchData = async () => {
    setLoading(true)
    setError(false)
    const res = await callAxios<DiagnosticsData>({ url: `/admin/diagnostics?user_id=${userId}` })
    if (res.success) {
      setData(res.data)
    } else {
      setError(true)
    }
    setLoading(false)
  }

  useEffect(() => {
    if (open) {
      fetchData()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, userId])

  const handleAction = async (diagId: number, action: string, reason?: string) => {
    const body: Record<string, unknown> = { action }
    if (reason) body.reason = reason
    await callAxios({ method: 'PATCH', url: `/admin/diagnostics/${diagId}` }, body)
    fetchData()
  }

  const handleAddComment = async () => {
    if (!commentText.trim()) return
    const deviceId = data?.diagnostics[0]?.device_id
    const payload: Record<string, unknown> = { body: commentText.trim() }
    if (deviceId) {
      payload.device_id = deviceId
    } else {
      payload.user_target_id = userId
    }
    await callAxios({ method: 'POST', url: '/admin/diagnostics/comments' }, payload)
    setCommentText('')
    fetchData()
  }

  const handleSettingChange = async (key: string, value: boolean) => {
    await callAxios(
      { method: 'PATCH', url: `/admin/diagnostics/settings/${userId}` },
      { [key]: value }
    )
    fetchData()
  }

  const activeIssues = data?.diagnostics.filter(d => d.status === 'open' || d.status === 'acknowledged') || []
  const resolvedIssues = data?.diagnostics.filter(d => d.status === 'resolved' || d.status === 'bypassed') || []
  const comments = data?.comments || []

  const formatDate = (d: string | null) => {
    if (!d) return ''
    return new Date(d).toLocaleDateString()
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ backgroundColor: '#1565c0', color: '#fff' }}>
        {t('Diag-title')} &mdash; {userEmail}
      </DialogTitle>
      <DialogContent sx={{ mt: 2 }}>
        {loading && <Typography sx={{ py: 2 }}>Loading...</Typography>}

        {error && (
          <Typography color="error" sx={{ py: 1 }}>
            Failed to load diagnostics data.
          </Typography>
        )}

        {!loading && (
          <>
            {/* Active Issues */}
            {data && (
              <>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 1, mb: 1 }}>
                  {t('Diag-active-issues')}
                </Typography>
                {activeIssues.length === 0 && (
                  <Typography color="text.secondary" sx={{ mb: 2 }}>{t('Diag-no-issues')}</Typography>
                )}
                {activeIssues.length > 0 && (
                  <Table size="small" sx={{ mb: 2 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ color: '#fff' }}>HW ID</TableCell>
                        <TableCell sx={{ color: '#fff' }}>{t('Diag-status')}</TableCell>
                        <TableCell sx={{ color: '#fff' }}>Message</TableCell>
                        <TableCell sx={{ color: '#fff' }}>{t('Diag-detected')}</TableCell>
                        <TableCell sx={{ color: '#fff' }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {activeIssues.map((diag: DiagnosticEntry) => (
                        <TableRow key={diag.id}>
                          <TableCell sx={{ color: '#fff' }}>{diag.hw_id || diag.device_id}</TableCell>
                          <TableCell sx={{ color: diag.severity === 'error' ? '#f44336' : '#ffa726' }}>
                            {t(`Diag-status-${diag.status}`)}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.85rem' }}>
                            <Link
                              to={`/help/diagnostics/${diag.check_type}`}
                              style={{ color: '#90caf9' }}
                              onClick={(e) => e.stopPropagation()}
                              target="_blank"
                            >
                              {t(`Diag-check-${diag.check_type}`)}: {diag.message}
                            </Link>
                          </TableCell>
                          <TableCell sx={{ color: '#fff' }}>{formatDate(diag.detected_at)}</TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                              {diag.status === 'open' && (
                                <Button size="small" variant="outlined" color="info"
                                  onClick={() => handleAction(diag.id, 'acknowledge')}>
                                  {t('Diag-acknowledge')}
                                </Button>
                              )}
                              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                                <TextField
                                  size="small"
                                  placeholder={t('Diag-bypass-reason')}
                                  value={bypassReason[diag.id] || ''}
                                  onChange={(e) => setBypassReason(prev => ({ ...prev, [diag.id]: e.target.value }))}
                                  sx={{ '& input': { fontSize: '0.8rem', py: 0.5 }, width: 180 }}
                                />
                                <Button size="small" variant="outlined" color="warning"
                                  disabled={!bypassReason[diag.id]?.trim()}
                                  onClick={() => handleAction(diag.id, 'bypass', bypassReason[diag.id])}>
                                  {t('Diag-bypass')}
                                </Button>
                              </Box>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}

                {/* Resolved Issues */}
                {resolvedIssues.length > 0 && (
                  <>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 2, mb: 1 }}>
                      {t('Diag-resolved')}
                    </Typography>
                    <Table size="small" sx={{ mb: 2 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ color: '#fff' }}>HW ID</TableCell>
                          <TableCell sx={{ color: '#fff' }}>{t('Diag-status')}</TableCell>
                          <TableCell sx={{ color: '#fff' }}>Message</TableCell>
                          <TableCell sx={{ color: '#fff' }}>{t('Diag-detected')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {resolvedIssues.map((diag: DiagnosticEntry) => (
                          <TableRow key={diag.id}>
                            <TableCell sx={{ color: '#fff' }}>{diag.hw_id || diag.device_id}</TableCell>
                            <TableCell sx={{ color: '#26a69a' }}>
                              {t(`Diag-status-${diag.status}`)}
                              {diag.bypass_reason && ` (${diag.bypass_reason})`}
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.85rem' }}>
                            <Link
                              to={`/help/diagnostics/${diag.check_type}`}
                              style={{ color: '#90caf9' }}
                              onClick={(e) => e.stopPropagation()}
                              target="_blank"
                            >
                              {t(`Diag-check-${diag.check_type}`)}: {diag.message}
                            </Link>
                          </TableCell>
                            <TableCell sx={{ color: '#fff' }}>
                              {formatDate(diag.detected_at)}
                              {diag.resolved_at && ` \u2192 ${formatDate(diag.resolved_at)}`}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </>
                )}
              </>
            )}

            {/* Comments — always visible */}
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 2, mb: 1 }}>
              {t('Diag-comments')}
            </Typography>
            {comments.map((comment: DeviceCommentEntry) => (
              <Box key={comment.id} sx={{ mb: 1, pl: 1, borderLeft: '2px solid #555' }}>
                <Typography variant="caption" color="text.secondary">
                  {formatDate(comment.inserted_at)} {comment.user_email}
                </Typography>
                <Typography variant="body2" sx={{ color: '#fff' }}>{comment.body}</Typography>
              </Box>
            ))}
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              <TextField
                size="small"
                fullWidth
                placeholder={t('Diag-add-comment')}
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleAddComment() }}
              />
              <Button variant="outlined" onClick={handleAddComment} disabled={!commentText.trim()}>
                {t('Diag-add-comment')}
              </Button>
            </Box>

            {/* Notification Settings */}
            {data && (
              <>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 3, mb: 1 }}>
                  {t('Diag-notification-settings')}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={data.settings.diagnostic_emails_installer}
                        onChange={(e) => handleSettingChange('diagnostic_emails_installer', e.target.checked)}
                      />
                    }
                    label={t('Diag-installer-emails')}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={data.settings.diagnostic_emails_customer}
                        onChange={(e) => handleSettingChange('diagnostic_emails_customer', e.target.checked)}
                      />
                    }
                    label={t('Diag-customer-emails')}
                  />
                </Box>
                {(() => {
                  const blocking = Array.from(new Set([
                    ...(data.blocking_groups?.installer || []),
                    ...(data.blocking_groups?.customer || [])
                  ]))
                  if (blocking.length === 0) return null
                  return (
                    <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
                      {t('Diag-blocked-by-groups', { groups: blocking.join(', ') })}
                    </Typography>
                  )
                })()}
              </>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}
